export default class BaseComponent {
  constructor(component) {
    this.debug = false;
    this.globalDebug = false;
    this.componentName = component.dataset.component;
    this.refs = {};
    this.dom = {
      $component: component,
    };
    this.state = {};
    this.mountedHandlers = [];
  }

  mount() {
    this.log('mount', this);
    this.bindToWindow();
  }

  $get(selector) {
    const { dom: { $component } } = this;
    return $component.querySelector(selector);
  }

  $getAll(selector) {
    const { dom: { $component } } = this;
    return Array.from($component.querySelectorAll(selector));
  }

  setClick(elementOrArray, callbackHandler, options = {}) {
    this.log('setClick', elementOrArray, options);

    this.addEventHandlersForElements('click', elementOrArray, callbackHandler, options);
  }

  setClickWithElement(elementOrArray, callbackHandler, options = {}) {
    this.log('setClickWithElement', elementOrArray, options);

    options.passElementToCallback = true;
    this.addEventHandlersForElements('click', elementOrArray, callbackHandler, options);
  }

  setOnSubmit(elementOrArray, callbackHandler, options = {}) {
    this.log('setOnSubmit', elementOrArray, options);

    this.addEventHandlersForElements('submit', elementOrArray, callbackHandler, options);
  }

  setOnEvent(eventName, elementOrArray, callbackHandler, options = {}) {
    this.log('setOnEvent', elementOrArray, options);

    this.addEventHandlersForElements(eventName, elementOrArray, callbackHandler, options);
  }

  setOnEventWithElement(eventName, elementOrArray, callbackHandler, options = {}) {
    this.log('setOnEventWithElement', elementOrArray, options);

    options.passElementToCallback = true;
    this.addEventHandlersForElements(eventName, elementOrArray, callbackHandler, options);
  }

  /**
   * Adds a listener for escape key to perform a given function, e.g. closing an overlay
   *
   * @param { [function reference] } callbackFunction a single reference to a function
   *
   * Usage Example:
   *
   * this.addEscapeListener(this.closeOverlay);
   *
  * */
  setOnEscapeKey(callbackHandler, options = {}) {
    this.log('setOnEscapeKey', `callbackHandler: ${callbackHandler.name}`, options);

    const builtHandler = this.buildEscapeKeyHandler(callbackHandler);

    this.addEventHandlerforElement('keydown', document, builtHandler, options);
  }

  setOnCustomEvent(eventName, callbackHandler, options = {}) {
    this.log('setOnCustomEvent', eventName, options);

    const builtHandler = (event) => {
      this.log(`Event received: ${eventName}`, event.detail);
      callbackHandler.call(this, event);
    };

    this.addEventHandlerforElement(eventName, document, builtHandler, options);
  }

  dispatch(eventName, eventDetail) {
    this.log(`Event dispatched: ${eventName}`, eventDetail);

    const customEvent = new CustomEvent(eventName, {
      detail: eventDetail,
    });

    document.dispatchEvent(customEvent);
  }

  log(message, ...args) {
    if (!this.debugMode()) return;

    if (typeof (message) === 'string') {
      console.log(`${this.componentName} / ${message}: `, ...args);
    } else {
      console.log(this.componentName, message, ...args);
    }
  }

  logWarn(message, ...args) {
    console.warn(`${this.componentName} / Warning: `, message, ...args);
  }

  logError(message, ...args) {
    console.error(`${this.componentName} / Error: `, message, ...args);
  }

  //
  // PRIVATE
  //

  buildBasicHandler(callbackHandler, callbackHandlerArgument = null, preventDefault = true) {
    const handler = (event) => {
      if (preventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }

      const arg = callbackHandlerArgument || event;
      callbackHandler.call(this, arg);
    };

    return handler;
  }

  buildEscapeKeyHandler(callbackHandler) {
    const handler = (event) => {
      const escapeKeyPressed = (event.keyCode === 27);

      if (escapeKeyPressed) {
        callbackHandler.call(this, event);
      }
    };

    return handler;
  }

  addEventHandlersForElements(eventName, elementOrArray, callbackHandler, options = {}) {
    const elements = this.prepareArray(elementOrArray);

    elements.forEach((element) => {
      // if `passElementToCallback` is true, callbackHandler will be called with
      // the element as the first argument, otherwise the event will be the first argument
      const passElementToCallback = options.passElementToCallback || false;
      const callbackHandlerArgument = passElementToCallback ? element : null;

      const builtHandler = this.buildBasicHandler(
        callbackHandler,
        callbackHandlerArgument,
        options.preventDefault,
      );
      this.addEventHandlerforElement(eventName, element, builtHandler, options);
    });
  }

  addBoundEventHandlerforElement(eventType, element, handlerFunction, options = {}) {
    const boundHandler = handlerFunction.bind(this);

    this.addEventHandlerforElement(eventType, element, boundHandler, options.listenerOptions);
  }

  addEventHandlerforElement(eventType, element, handlerFunction, options = {}) {
    element.addEventListener(eventType, handlerFunction, options.listenerOptions);
    this.addMountedEntry(element, eventType, handlerFunction, options.listenerOptions);
  }

  addMountedEntry(element, eventType, handlerFunction, listenerOptions) {
    const listenerArgs = [
      eventType,
      handlerFunction,
    ];
    const mountedEntry = {
      target: element,
      args: listenerArgs,
      listenerOptions,
    };

    this.mountedHandlers.push(mountedEntry);
  }

  unmount() {
    const { mountedHandlers } = this;
    this.log('unmount', mountedHandlers, this);

    let mountedEntry = mountedHandlers.shift();
    while (typeof mountedEntry !== 'undefined') {
      mountedEntry = mountedHandlers.shift();
      this.removeMountedEntry(mountedEntry);
    }
  }

  removeMountedEntry(mountedEntry) {
    const { target, args, listenerOptions } = mountedEntry;

    const selectedOptions = {
      capture: listenerOptions.capture || false,
    };

    target.removeEventListener(...args, selectedOptions);
  }

  bindToWindow() {
    if (!this.debugMode()) return;

    window.theme = window.theme || {};
    const existingVal = window.theme[this.componentName];

    if (existingVal) {
      window.theme[this.componentName] = Array.isArray(existingVal) ? existingVal : [existingVal];
      window.theme[this.componentName].push(this);
    } else {
      window.theme[this.componentName] = this;
    }

    this.log('bindToWindow', `debug this component in the browser console with "window.theme.${this.componentName}"`);
  }

  prepareArray(elementOrArray) {
    return Array.isArray(elementOrArray) ? elementOrArray : Array.of(elementOrArray);
  }

  debugMode() {
    return this.globalDebug || this.debug;
  }
}
